import React, { useState, useEffect } from 'react';
import { createStorefrontApiClient } from '@shopify/storefront-api-client';
import { Context } from './context';
import { LocalStorage, LocalStorageKeys } from './utils';
import { CART_QUERY, CART_CREATE_MUTATION, normalizeCart } from './cart-graphql';

export function ContextProvider({ shopName, accessToken, children }) {
  if (shopName == null || accessToken == null) {
    throw new Error(
      'Unable to build shopify-buy client object. Please make sure that your access token and domain are correct.',
    );
  }

  const initialCart = LocalStorage.getInitialCart();
  const [cart, setCart] = useState(initialCart);
  const [client, setClient] = useState(null);
  const [isInitializing, setIsInitializing] = useState(false);
  // Skip validation when there's no persisted cart — createNewCart handles it.
  const [hasValidatedInitialCart, setHasValidatedInitialCart] = useState(
    initialCart == null,
  );

  const isCustomDomain = shopName.includes('.');
  const storeDomain = isCustomDomain ? shopName : `${shopName}.myshopify.com`;

  useEffect(() => {
    const shopifyClient = createStorefrontApiClient({
      storeDomain,
      publicAccessToken: accessToken,
      apiVersion: '2025-01',
    });
    setClient(shopifyClient);
  }, [storeDomain, accessToken]);

  // Shopify carts expire after ~10 days of inactivity. Validate the persisted
  // cart against the Storefront API on first load; if it's gone, drop it so a
  // fresh cart is created downstream.
  useEffect(() => {
    if (!client || hasValidatedInitialCart) return;

    // Capture the persisted id up front — `cart` is intentionally excluded from
    // the dependency array (this runs exactly once, before any cart mutation).
    const cartId = initialCart?.id;
    if (cartId == null) {
      setHasValidatedInitialCart(true);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await client.request(CART_QUERY, {
          variables: { cartId },
        });
        if (cancelled) return;
        // `client.request` resolves (does not throw) on transport/GraphQL
        // failures, surfacing them in `response.errors` with no `data`. That is
        // NOT a sign the cart expired — keep the persisted cart so a network
        // blip doesn't wipe a valid cart. The add-to-cart path self-heals a
        // genuinely-dead cart on the next interaction.
        if (response?.errors || !response?.data) return;
        // `data.cart === null` is the genuine "cart expired/deleted" signal.
        const fresh = response.data.cart;
        // Only act if the cart is still the one we validated. A concurrent
        // add-to-cart can self-heal an expired cart while this query is in
        // flight; without this guard, a late `data.cart === null` would wipe
        // that newer, valid cart. The functional updater reads the latest
        // committed state (cart is intentionally not in the dep array).
        setCart((prev) =>
          prev?.id === cartId ? (fresh ? normalizeCart(fresh) : null) : prev,
        );
      } catch (error) {
        if (cancelled) return;
        // Thrown network/abort error — preserve the cart, don't destroy it.
        console.error('Error validating cart, keeping persisted cart:', error);
      } finally {
        if (!cancelled) setHasValidatedInitialCart(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [client, hasValidatedInitialCart]);

  useEffect(() => {
    async function createNewCart() {
      if (!client || isInitializing) return;

      setIsInitializing(true);
      try {
        const response = await client.request(CART_CREATE_MUTATION, {
          variables: { input: {} },
        });

        if (response?.data?.cartCreate?.cart) {
          setCart(normalizeCart(response.data.cartCreate.cart));
        }
      } catch (error) {
        console.error('Error creating cart:', error);
      } finally {
        setIsInitializing(false);
      }
    }

    if (
      cart == null &&
      client &&
      !isInitializing &&
      hasValidatedInitialCart
    ) {
      createNewCart();
    }
  }, [cart, client, isInitializing, hasValidatedInitialCart]);

  useEffect(() => {
    if (cart) {
      LocalStorage.set(LocalStorageKeys.CART, JSON.stringify(cart));
    } else {
      // Cart was dropped (expired/invalid). Clear the stale entry so the next
      // load doesn't resurrect a dead cart id and repeat the validate->fail loop.
      LocalStorage.remove(LocalStorageKeys.CART);
    }
  }, [cart]);

  return (
    <Context.Provider
      value={{
        client,
        cart,
        setCart,
        isInitializing,
      }}
    >
      {children}
    </Context.Provider>
  );
}
